import { useEffect, useRef, useState } from 'react';
import { LuChevronDown, LuChevronUp } from 'react-icons/lu';
import ProjectAbout from '../ProjectAbout/ProjectAbout';
import ProjectData from '../ProjectData/ProjectData';
import ProjectProcess from '../ProjectProcess/ProjectProcess';
import ProjectTasks from '../ProjectTasks/ProjectTasks';
import {
  keepSectionTopDuringExpand,
  scrollSectionAfterCollapse,
} from '../../../utils/scrollAfterCollapse';
import './style.css';

const MOBILE_QUERY = '(max-width: 767px)';
const BORDER_FLASH_DURATION = 220;

const useMobileViewport = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(MOBILE_QUERY).matches
      : false
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const handleChange = event => setIsMobile(event.matches);

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isMobile;
};

const ProjectIntroAccordion = ({ project, projectId, enabled = false }) => {
  const [openSection, setOpenSection] = useState(null);
  const [closingSection, setClosingSection] = useState(null);
  const closingTimerRef = useRef(null);
  const itemRefs = useRef({});
  const isMobile = useMobileViewport();

  useEffect(() => {
    if (!enabled || !isMobile) {
      window.clearTimeout(closingTimerRef.current);
      setOpenSection(null);
      setClosingSection(null);
    }
  }, [enabled, isMobile]);

  useEffect(
    () => () => window.clearTimeout(closingTimerRef.current),
    []
  );

  if (!enabled) {
    return (
      <>
        <div className="project-sections-grid">
          <ProjectAbout data={project.about} />
          <ProjectData data={project.sourceData} />
          <ProjectTasks data={project.tasks} />
        </div>
        <ProjectProcess data={project.process} />
      </>
    );
  }

  const copy = project.introAccordion || {};

  const sections = [
    {
      id: 'about',
      title: copy.aboutTitle || 'О проекте',
      hint: copy.aboutHint || 'Контекст, выполненные работы и результат',
      content: <ProjectAbout data={project.about} />,
    },
    {
      id: 'source-data',
      title: copy.sourceDataTitle || 'Исходные данные и требования',
      hint: copy.sourceDataHint || 'Формат облака, охват и требования к чертежам',
      content: <ProjectData data={project.sourceData} />,
    },
    {
      id: 'tasks',
      title: copy.tasksTitle || 'Задачи проекта',
      hint: copy.tasksHint || 'Основные задачи при подготовке чертежей',
      content: <ProjectTasks data={project.tasks} />,
    },
  ];
  const processSection = {
    id: 'process',
    title: copy.processTitle || 'Процесс работы',
    hint: copy.processHint || 'Этапы подготовки и выдачи документации',
    content: <ProjectProcess data={project.process} />,
  };

  const closeSection = sectionId => {
    const card = itemRefs.current[sectionId];
    window.clearTimeout(closingTimerRef.current);
    scrollSectionAfterCollapse(card);
    setClosingSection(sectionId);
    setOpenSection(null);
    closingTimerRef.current = window.setTimeout(
      () => setClosingSection(null),
      BORDER_FLASH_DURATION
    );
  };

  return (
    <div className="project-sections-grid project-intro-accordion">
      {[...sections, processSection].map(({ id, title, hint, content }) => {
        const isOpen = isMobile && openSection === id;
        const isClosing = isMobile && closingSection === id;
        const panelId = `project-${projectId}-${id}-panel`;
        const headingId = `project-${projectId}-${id}-heading`;

        return (
          <div
            className={`project-intro-accordion__item project-case-card${isOpen ? ' is-open' : ''}${isClosing ? ' is-closing' : ''}${id === 'process' ? ' project-intro-accordion__process-item' : ''}`}
            key={id}
            ref={element => {
              itemRefs.current[id] = element;
            }}
          >
            <h2 className="project-intro-accordion__heading" id={headingId}>
              <button
                type="button"
                className="project-intro-accordion__toggle"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => {
                  if (isOpen) {
                    closeSection(id);
                    return;
                  }

                  window.clearTimeout(closingTimerRef.current);
                  setClosingSection(null);
                  keepSectionTopDuringExpand(itemRefs.current[id]);
                  setOpenSection(id);
                }}
              >
                <span className="project-intro-accordion__heading-copy">
                  <span className="project-intro-accordion__title">
                    {title}
                  </span>
                  <span className="project-intro-accordion__hint">
                    <span>{hint}</span>
                  </span>
                </span>
                {isOpen ? (
                  <LuChevronUp
                    className="project-intro-accordion__chevron"
                    aria-hidden="true"
                  />
                ) : (
                  <LuChevronDown
                    className="project-intro-accordion__chevron"
                    aria-hidden="true"
                  />
                )}
              </button>
            </h2>

            <div
              className="project-intro-accordion__panel"
              id={panelId}
              aria-labelledby={headingId}
              aria-hidden={isMobile ? !isOpen : undefined}
            >
              <div className="project-intro-accordion__panel-inner">
                {content}
                <button
                  type="button"
                  className="project-intro-accordion__collapse"
                  aria-controls={panelId}
                  onClick={() => closeSection(id)}
                >
                  <span>{copy.collapseLabel || 'Свернуть'}</span>
                  <LuChevronUp aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProjectIntroAccordion;
